import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

/**
 * PAGES THAT SCROLL (2026-10-02).
 *
 * The reading areas — the story, the conversation with the coach, a closed
 * casebook — are still books with pages, but a page is now a CHAPTER'S WORTH
 * that scrolls, not a screen's worth that is cut to fit. Three rules replace
 * the measuring column that used to decide where a page ended:
 *
 *   1. A PAGE IS DECIDED FROM THE CONTENT, NEVER FROM THE GEOMETRY. Where a
 *      page begins is a function of the atoms before it and nothing else —
 *      an authored break (a new chapter), or an exchange beginning once the
 *      page already holds a chapter's worth of words. Resizing the window,
 *      a font landing, the composer growing: none of them can move a word
 *      from one page to another, because none of them is an input.
 *
 *   2. ONCE A WORD IS ON A PAGE IT STAYS THERE. The decision for an exchange
 *      is made from the exchanges BEFORE it, whose words are already settled,
 *      so a turn landing, a reply being typed out, or a draft becoming a kept
 *      line can only ever ADD to the end of the last page or open a new one
 *      after it. (The first version re-cut the whole thread on every change
 *      and re-balanced the last two pages when the final one came out thin;
 *      a learner watched their own sentence slide to the previous page while
 *      the coach was still answering it.)
 *
 *   3. WITHIN A PAGE, THE READER SCROLLS. The page is a plain scroll
 *      container. Nothing is clipped, nothing is cut mid-paragraph, and a
 *      table is as tall as it is.
 *
 * What is kept from before: the vocabulary of ATOMS (one paragraph, one line
 * said, one control), the page control that turns between pages, and the
 * promise that the whole history is always reachable.
 */

/** Prose. The component owns the markup so the text is one node, which is what the scanners and the tests read. */
export interface TextItem {
  /** Starts a new page: a chapter head in the casebook, a new day with the coach. */
  readonly breakBefore?: boolean;
  /**
   * This atom begins an EXCHANGE — a line the learner said, the coach's word on
   * arrival, a chapter head — and a page may begin here once the page before
   * it is full. It is also what "the newest thing" means: the last anchor is
   * where the reader is carried to when they are following the story.
   */
  readonly anchor?: boolean;
  /**
   * How much of a page this atom takes, for the budget. By default a text
   * atom weighs its characters and everything else weighs nothing. The coach
   * sets it explicitly on a paragraph being typed, so that the weight of a
   * reply is the whole reply from its first character — a page must not fill
   * up part-way through the answer.
   */
  readonly weight?: number;
  readonly key: string;
  readonly kind: "text";
  readonly text: string;
  readonly className: string;
  /** Drawn before the text (a redaction's bar). */
  readonly lead?: ReactNode;
  /** Drawn after the text (a margin note's author). */
  readonly tail?: ReactNode;
}

/**
 * Something with countable parts — a table's rows, a list's items. The
 * renderer still takes `(from, to, continued)`, because the documents' block
 * renderers are written against that shape; on a scrolling page every part is
 * always drawn, so it is called with the whole range.
 */
export interface UnitsItem {
  readonly breakBefore?: boolean;
  readonly anchor?: boolean;
  readonly weight?: number;
  readonly key: string;
  readonly kind: "units";
  readonly units: number;
  readonly node: (from: number, to: number | null, continued: boolean) => ReactNode;
}

/** A heading, a letterhead, a control: one piece, drawn whole. */
export interface BlockItem {
  readonly breakBefore?: boolean;
  readonly anchor?: boolean;
  readonly weight?: number;
  readonly key: string;
  readonly kind: "block";
  readonly node: () => ReactNode;
}

export type PageItem = TextItem | UnitsItem | BlockItem;

/** One page: its atoms, keyed by the first of them. */
export interface Page {
  readonly key: string;
  readonly items: readonly PageItem[];
}

/**
 * A CHAPTER'S WORTH, in characters of prose: about thirteen hundred words,
 * which is five or six screens of the conversation panel at a laptop size.
 * The reader scrolls inside it and turns a page only at the seams the content
 * itself has — a new in-game day, a new day with the coach, or this much read since the last one.
 * The one knob the page model has; everything else is the content's own.
 */
export const PAGE_BUDGET = 8000;

export function weightOf(item: PageItem): number {
  if (item.weight !== undefined) return item.weight;
  return item.kind === "text" ? item.text.length : 0;
}

/**
 * Cut the atoms into pages. A page begins at an atom with `breakBefore`, or
 * at an `anchor` once the page before it already holds `budget` worth. The
 * first atom never opens an empty page, and an empty list is still one page,
 * so a page control always has a page to show.
 *
 * PREFIX-STABLE, by construction: the page an atom lands on depends only on
 * the atoms before it. Appending atoms, or replacing the LAST anchor and what
 * follows it (a pending line becoming the kept one), cannot move anything
 * that came earlier.
 */
export function splitPages(items: readonly PageItem[], budget: number = PAGE_BUDGET): readonly Page[] {
  const pages: Page[] = [];
  let current: PageItem[] = [];
  let weight = 0;
  for (const [index, item] of items.entries()) {
    const forced = item.breakBefore === true && index > 0;
    const full = item.anchor === true && weight >= budget;
    if ((forced || full) && current.length > 0) {
      pages.push(pageOf(current));
      current = [];
      weight = 0;
    }
    current.push(item);
    weight += weightOf(item);
  }
  pages.push(pageOf(current));
  return pages;
}

function pageOf(items: readonly PageItem[]): Page {
  return { key: items[0]?.key ?? "", items };
}

/** The newest exchange: the last anchor, and a serial that changes exactly when an exchange is added. */
export interface Newest {
  readonly serial: number;
  readonly key: string;
}

export function newestOf(items: readonly PageItem[]): Newest | null {
  let serial = 0;
  let key: string | null = null;
  for (const item of items) {
    if (item.anchor !== true) continue;
    serial += 1;
    key = item.key;
  }
  return key === null ? null : { serial, key };
}

/** One atom, drawn whole. */
export function renderItem(item: PageItem): ReactNode {
  switch (item.kind) {
    case "text":
      return (
        <p className={item.className} data-pcs-text="">
          {item.lead}
          {item.text}
          {item.tail}
        </p>
      );
    case "units":
      return item.node(0, null, false);
    case "block":
      return item.node();
  }
}

/** The atoms of a page, each in its wrapper. Shared by the reading areas and the paper windows. */
export function PageAtoms({ items }: { readonly items: readonly PageItem[] }): ReactNode {
  return items.map((item) => (
    <div key={item.key} className="pcs-atom" data-pcs-atom={item.key}>
      {renderItem(item)}
    </div>
  ));
}

/**
 * THE READING AREA: one page, scrolling.
 *
 * What moves the reader, and what never does:
 *
 *   - A PAGE TURN opens the page where the reader last left it, or at its top.
 *   - THE NEWEST EXCHANGE arriving (`newest.serial` changes) carries a reader
 *     who is FOLLOWING to its first line — the line they just said, with the
 *     answer under it — and then leaves them alone. The answer may go on being
 *     typed below the fold; it is read by scrolling, at the reader's pace. A
 *     reply typed out to the bottom of the viewport at ninety characters a
 *     second would scroll its own first paragraph away before anyone had read
 *     it, which is exactly what the first scrolling version of the front door
 *     did (2026-09-10).
 *   - "LATEST" (`latestTick`) does the same on request.
 *   - Nothing else. A resize, a font, the composer growing under the page:
 *     the reader's place holds. The one courtesy: a reader at the very bottom
 *     stays at the bottom when the page shrinks, so the composer growing
 *     under them does not cover the last line.
 */
export function ScrollPage({
  page,
  className = "pcs-page",
  label,
  live = false,
  lead,
  newest,
  following,
  latestTick,
  onTopKey,
  showKey = null,
}: {
  readonly page: Page;
  readonly className?: string;
  readonly label?: string;
  /** Announce what lands here: true for a conversation, false for a document. */
  readonly live?: boolean;
  /** Drawn at the head of the page, before its atoms: the coach's portrait, which the prose wraps around. */
  readonly lead?: ReactNode;
  readonly newest: Newest | null;
  /** The reader is on the last page and following the story. */
  readonly following: boolean;
  /** Bumped by the page control's "Latest". */
  readonly latestTick: number;
  /** Reports the first atom at or under the top edge as the reader scrolls — how a closed book's scene follows the reading. */
  readonly onTopKey?: ((key: string | null) => void) | undefined;
  /**
   * An atom asked for from outside the page (the desk's "why this case?",
   * 2026-10-31): when the tick changes and the atom is on this page, the
   * page scrolls to it, after any turn it took to get here. An atom not on
   * this page is left alone: the caller turns the page first.
   */
  readonly showKey?: { readonly key: string; readonly tick: number } | null | undefined;
}): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  /** Where the reader left each page, by page key. */
  const offsets = useRef(new Map<string, number>());
  const shownPage = useRef<string | null>(null);
  const shownSerial = useRef<number | null>(null);
  const shownTick = useRef(latestTick);
  const atBottom = useRef(false);
  /**
   * The atom the page was last scrolled to on purpose, and the scrollTop that
   * put it there — until the reader scrolls away. A font landing after the
   * first paint reflows the page (the display face is a few lines taller than
   * its fallback), and the newest exchange was found a hundred pixels adrift
   * of where it had just been put; once the fonts are in it is put back.
   */
  const pinned = useRef<{ readonly key: string; readonly top: number } | null>(null);
  const [fontsTick, setFontsTick] = useState(0);
  const report = useRef(onTopKey);
  report.current = onTopKey;

  const reportTop = useCallback(() => {
    const root = ref.current;
    const callback = report.current;
    if (root === null || callback === undefined) return;
    const rootTop = root.getBoundingClientRect().top;
    let found: string | null = null;
    for (const atom of root.querySelectorAll<HTMLElement>("[data-pcs-atom]")) {
      if (atom.getBoundingClientRect().bottom - rootTop > 1) {
        found = atom.dataset["pcsAtom"] ?? null;
        break;
      }
    }
    callback(found);
  }, []);

  const scrollToKey = useCallback((key: string) => {
    const root = ref.current;
    if (root === null) return;
    const atom = root.querySelector<HTMLElement>(`[data-pcs-atom="${cssEscape(key)}"]`);
    // The browser clamps to the end of the content, so a short newest
    // exchange sits at the foot of the page with what came before above it.
    root.scrollTop = atom === null ? root.scrollHeight : atom.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    pinned.current = { key, top: root.scrollTop };
  }, []);

  useEffect(() => {
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts?.ready === undefined) return;
    let live = true;
    void fonts.ready.then(() => { if (live) setFontsTick((n) => n + 1); });
    return () => { live = false; };
  }, []);
  useLayoutEffect(() => {
    if (fontsTick > 0 && pinned.current !== null) scrollToKey(pinned.current.key);
  }, [fontsTick, scrollToKey]);

  const serial = newest?.serial ?? null;
  const newestKey = newest?.key ?? null;
  useLayoutEffect(() => {
    const root = ref.current;
    if (root === null) return;
    const turned = shownPage.current !== page.key;
    const newer = serial !== null && shownSerial.current !== serial;
    const asked = shownTick.current !== latestTick;
    shownPage.current = page.key;
    shownSerial.current = serial;
    shownTick.current = latestTick;
    if (newestKey !== null && (asked || (newer && following))) scrollToKey(newestKey);
    else if (turned) {
      root.scrollTop = offsets.current.get(page.key) ?? 0;
      pinned.current = null;
    }
    reportTop();
  }, [page.key, serial, newestKey, following, latestTick, scrollToKey, reportTop]);

  // Declared after the turn above, so it runs after it and has the last word on where the page stands.
  const showTick = showKey?.tick ?? null;
  const showAtom = showKey?.key ?? null;
  useLayoutEffect(() => {
    const root = ref.current;
    if (root === null || showTick === null || showAtom === null) return;
    if (root.querySelector(`[data-pcs-atom="${cssEscape(showAtom)}"]`) === null) return;
    scrollToKey(showAtom);
    reportTop();
  }, [showTick, showAtom, page.key, scrollToKey, reportTop]);

  // The reader's own scrolling: remembered per page, and reported for the book.
  useEffect(() => {
    const root = ref.current;
    if (root === null) return;
    let frame = 0;
    const onScroll = (): void => {
      offsets.current.set(shownPage.current ?? "", root.scrollTop);
      atBottom.current = root.scrollTop + root.clientHeight >= root.scrollHeight - 2;
      // The reader moved: the pin is theirs to drop.
      if (pinned.current !== null && Math.abs(root.scrollTop - pinned.current.top) > 1) pinned.current = null;
      if (frame !== 0 || report.current === undefined) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        reportTop();
      });
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [reportTop]);

  // The page shrinking under a reader at its foot (the composer growing as
  // they write): keep the foot in view. Everyone else keeps their top line.
  useEffect(() => {
    const root = ref.current;
    if (root === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (atBottom.current) root.scrollTop = root.scrollHeight;
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className={`${className} pc-scroll`} data-region="page" aria-label={label} aria-live={live ? "polite" : undefined}>
      {lead}
      <PageAtoms items={page.items} />
    </div>
  );
}

/** `CSS.escape` is not in jsdom's older surfaces; keys here are our own ids, so a conservative fallback is enough. */
function cssEscape(value: string): string {
  const css = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
  return css?.escape === undefined ? value.replaceAll(/["\\]/g, "\\$&") : css.escape(value);
}

/**
 * WHERE THE READER IS: which page, and whether they are following the story.
 *
 * "latest": the story and the coach, read forwards — a new page opening
 * carries a reader who was on the last page to it, and leaves a reader who
 * has turned back exactly where they are until they ask for Latest.
 * "start": a closed book, opened at its first page.
 *
 * Derived, not synchronised: the page is computed from what the reader last
 * chose and the page count at the time they chose it, so a count change in
 * the same render as a turn lands is answered in that render.
 */
export interface ReadingPlace {
  readonly page: number;
  readonly count: number;
  readonly following: boolean;
  readonly setPage: (next: number) => void;
  readonly toLatest: () => void;
  /** Changes each time Latest is asked for, so the page can scroll to the newest exchange. */
  readonly latestTick: number;
}

export function useReadingPlace(pageCount: number, follow: "latest" | "start" = "latest"): ReadingPlace {
  const count = Math.max(pageCount, 1);
  const last = count - 1;
  /** The page the reader chose, and how many there were when they chose it. Null until they touch the control. */
  const [chosen, setChosen] = useState<{ readonly page: number; readonly count: number } | null>(null);
  const [latestTick, setLatestTick] = useState(0);
  const countRef = useRef(count);
  countRef.current = count;

  const page = (() => {
    if (chosen === null) return follow === "latest" ? last : 0;
    // On the last page when the count was what it was: still following.
    if (chosen.count !== count && chosen.page >= chosen.count - 1) return last;
    return Math.min(chosen.page, last);
  })();

  const setPage = useCallback((next: number) => {
    const latest = Math.max(countRef.current - 1, 0);
    setChosen({ page: Math.min(Math.max(next, 0), latest), count: countRef.current });
  }, []);
  const toLatest = useCallback(() => {
    setChosen({ page: Math.max(countRef.current - 1, 0), count: countRef.current });
    setLatestTick((tick) => tick + 1);
  }, []);

  return { page, count, following: page >= last, setPage, toLatest, latestTick };
}
